import React, { useEffect, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Gift, Copy, Share2, Link2, Users, TrendingUp, Trophy, CheckCircle2, Clock,
  Mail, MessageSquare, Sparkles, ArrowRight,
} from "lucide-react";

/**
 * ClientReferrals — "Refer & Earn" page for clients (and staff).
 * Any logged-in user gets a personal referral code, share tools, live stats,
 * and a list of who they've referred. Admins still handle intake — this just
 * tracks attribution and gives clients an easy way to spread the word.
 */
export default function ClientReferrals() {
  const [codeDoc, setCodeDoc] = useState(null);
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/client/my-referral"),
        api.get("/client/my-referrals"),
      ]);
      setCodeDoc(c?.code || null);
      setRefs(r?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Always show the clean production domain in share links — never the preview URL.
  // The /r/:code route redirects to /register?ref=CODE and tracks the click.
  const SHARE_HOST = "clientquack.com";
  const code = codeDoc?.code || "";
  const url = code ? `https://${SHARE_HOST}/r/${code}` : "";

  const copyLink = () => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied — paste anywhere"));
  };
  const copyCode = () => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => toast.success(`Code ${code} copied`));
  };
  const share = async () => {
    if (!url) return;
    const text = `Get your credit tracked with Credit Quack — signup takes 30 seconds. Use my link:`;
    if (navigator.share) {
      try { await navigator.share({ title: "Credit Quack", text, url }); return; } catch {}
    }
    copyLink();
  };
  const smsShare = () => {
    const body = encodeURIComponent(`Check out Credit Quack — they track your credit repair in real time. Use my link: ${url}`);
    window.location.href = `sms:?&body=${body}`;
  };
  const emailShare = () => {
    const body = encodeURIComponent(
      `Hey! I've been using Credit Quack to track my credit repair and it's the most transparent thing I've ever seen — you literally watch every stage happen in real time.\n\nSign up here (30 seconds): ${url}\n\nOr use my code at signup: ${code}\n`
    );
    const subject = encodeURIComponent("You should check out Credit Quack");
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const signups = codeDoc?.signups || 0;
  const clicks = codeDoc?.clicks || 0;
  const conv = clicks > 0 ? Math.round((signups / clicks) * 100) : 0;

  return (
    <AppShell>
      <div className="cq-in max-w-5xl">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] font-black text-amber-600">
          <Gift size={12} /> Refer & Earn
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">Share Credit Quack with your people</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Every friend who signs up with your code gets our full walkthrough plus a real kickoff call with us — no cold intake, no confusion. You'll see them appear here the moment they register.
        </p>

        {/* Code + share panel */}
        <Card className="mt-6 p-6 rounded-3xl border-slate-100 bg-gradient-to-br from-amber-50 via-white to-rose-50 relative overflow-hidden" data-testid="client-ref-code-card">
          <div className="cq-grain absolute inset-0 opacity-40 pointer-events-none" />
          {loading ? (
            <p className="text-sm text-slate-500">Loading your code…</p>
          ) : code ? (
            <div className="relative grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6 items-center">
              <button
                onClick={copyCode}
                className="group text-left"
                data-testid="client-ref-code"
                title="Click to copy code"
              >
                <p className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-600">Your code</p>
                <p className="font-display font-black text-5xl sm:text-6xl text-slate-900 tracking-tighter mt-1 group-hover:text-amber-600 transition">
                  {code}
                </p>
                <p className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1"><Copy size={10} /> Tap to copy</p>
              </button>

              <div>
                <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Your share link</p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <div className="flex-1 min-w-[220px] inline-flex items-center gap-2 px-3 h-11 rounded-full bg-white border border-slate-200 text-sm text-slate-700 truncate">
                    <Link2 size={14} className="text-slate-400 shrink-0" /> <span className="truncate font-mono">{url.replace(/^https?:\/\//, "")}</span>
                  </div>
                  <Button onClick={copyLink} variant="outline" className="h-11 rounded-full" data-testid="client-ref-copy">
                    <Copy size={14} className="mr-1" /> Copy
                  </Button>
                  <Button onClick={share} className="h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="client-ref-share">
                    <Share2 size={14} className="mr-1" /> Share
                  </Button>
                </div>

                <div className="mt-4 flex gap-2 flex-wrap">
                  <Button onClick={smsShare} variant="outline" className="rounded-full" data-testid="client-ref-sms">
                    <MessageSquare size={14} className="mr-1" /> Text a friend
                  </Button>
                  <Button onClick={emailShare} variant="outline" className="rounded-full" data-testid="client-ref-email">
                    <Mail size={14} className="mr-1" /> Email a friend
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Your code will appear here in a moment.</p>
          )}
        </Card>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard testid="client-ref-clicks"  icon={<TrendingUp size={16} />} label="Link clicks"        value={clicks} tone="sky" />
          <StatCard testid="client-ref-signups" icon={<Users size={16} />}      label="Friends signed up"  value={signups} tone="amber" />
          <StatCard testid="client-ref-conv"    icon={<Trophy size={16} />}     label="Conversion"         value={`${conv}%`} tone="emerald" />
        </div>

        {/* How it works */}
        <Card className="mt-6 p-5 rounded-2xl border-slate-100" data-testid="client-ref-how">
          <p className="text-[11px] uppercase tracking-widest font-black text-slate-500 flex items-center gap-1"><Sparkles size={12} /> How it works</p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { n: 1, t: "Share your link", d: "Tap Text, Email, or Share — send it to anyone who's talked about wanting better credit." },
              { n: 2, t: "They sign up with your code", d: "Your code auto-fills. They get our welcome flow + a booked kickoff call with our team." },
              { n: 3, t: "You get credit + updates", d: "You'll see them right here with intake status. We handle the rest — no chasing needed." },
            ].map((s) => (
              <div key={s.n} className="rounded-xl border border-slate-100 bg-white p-4">
                <div className="h-7 w-7 rounded-full bg-amber-500 text-white flex items-center justify-center font-black">{s.n}</div>
                <p className="font-display font-black mt-2 text-slate-900">{s.t}</p>
                <p className="text-sm text-slate-600 mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Referred users */}
        <div className="mt-6">
          <h2 className="font-display font-black text-2xl text-slate-900 flex items-center gap-2"><Users size={20} className="text-emerald-500" /> Friends you've referred</h2>
          <p className="text-sm text-slate-500 mt-1">
            Everyone who signed up using <b>{code || "your code"}</b>. We reach out to each of them ourselves — you just watch it happen.
          </p>
          <Card className="mt-3 rounded-2xl border-slate-100 divide-y">
            {refs.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-400 flex flex-col items-center gap-2" data-testid="client-ref-empty">
                <Gift size={28} className="text-slate-300" />
                <p>Nobody yet — share your link to get started.</p>
                <Button onClick={share} className="mt-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                  <Share2 size={14} className="mr-1" /> Share now <ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            )}
            {refs.map((u) => (
              <div key={u.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`client-ref-user-${u.id}`}>
                <div className="h-10 w-10 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold">{(u.name || "?")[0]}</div>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-display font-black text-slate-900">{u.name}</p>
                  <p className="text-xs text-slate-500">
                    Signed up {new Date(u.created_at).toLocaleDateString()}
                    {u.call_scheduled_at ? ` · Call: ${new Date(u.call_scheduled_at).toLocaleString()}` : ""}
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-black rounded-full border px-2 py-0.5 ${u.intake_done ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {u.intake_done ? <span className="inline-flex items-center gap-1"><CheckCircle2 size={10}/> Onboarded</span> : <span className="inline-flex items-center gap-1"><Clock size={10}/> Pending</span>}
                </span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function StatCard({ icon, label, value, tone = "amber", testid }) {
  const tones = {
    amber:   "bg-amber-50 text-amber-700 border-amber-100",
    sky:     "bg-sky-50 text-sky-700 border-sky-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  };
  return (
    <Card className={`p-3 rounded-2xl border ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold">{icon}{label}</div>
      <p className="font-display font-black text-2xl mt-1 text-slate-900">{value}</p>
    </Card>
  );
}
